/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { USER_MY_PROJECT_VIEW } from "../../service/apiUrl";
import { postApiCall } from "../../service/PostApiCall";
import { Skeleton, SkeletonText } from "../../components/Skeleton";
import MarkdownView from "../../components/MarkdownView";
import { refreshNotifications } from "../../utility/notificationsFeed";

/* ============================================================================
   One of the employee's projects: the description an admin wrote in the
   markdown editor (images included), plus links and team. Read-only.

   The API answers "Project not found" for a project the user is not on, so
   this page cannot be used to read other projects by id. Loading it marks
   the user's "added to" notification for it read, so the bell and the
   banner are asked to catch up as soon as it lands.

   Same reveal as My Projects: blocks fade in on load; the body is a
   `reveal-swap` from a skeleton shaped like it.
   ========================================================================== */
const People = ({ label, people }) =>
  people?.length ? (
    <div className="mpv-fact">
      <span className="mpv-fact-label">{label}</span>
      <span className="mpv-fact-value">{people.map((p) => p.name).join(", ")}</span>
    </div>
  ) : null;

const LinkFact = ({ label, href }) =>
  href ? (
    <div className="mpv-fact">
      <span className="mpv-fact-label">{label}</span>
      <a className="mpv-fact-value mpv-link" href={href} target="_blank" rel="noreferrer">
        {href.replace(/^https?:\/\//, "")}
      </a>
    </div>
  ) : null;

const BodySkeleton = () => (
  <div className="mpv-body" aria-hidden="true">
    <aside className="mpv-facts card-input">
      {[0, 1, 2].map((i) => (
        <div className="mpv-fact" key={i}>
          <Skeleton width="40%" height={11} />
          <Skeleton width="75%" height={13} style={{ marginTop: 6 }} />
        </div>
      ))}
    </aside>
    <article className="mpv-doc card-input">
      <Skeleton width="45%" height={22} />
      <SkeletonText lines={4} />
      <Skeleton height={160} style={{ marginTop: 16 }} />
      <SkeletonText lines={3} />
    </article>
  </div>
);

const MyProjectView = (props) => {
  const { projectId } = useParams();
  const token = useSelector((state) => state.signinReducers)?.data?.access_token;
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || !projectId) return;
    setProject(null);
    setError("");
    postApiCall({ id: projectId }, token, USER_MY_PROJECT_VIEW)
      .then((result) => {
        setProject(result);
        refreshNotifications();
      })
      .catch((err) => setError(err?.data?.message || "This project could not be loaded"));
  }, [token, projectId]);

  return (
    <div className={props?.data?.transtion === true ? "contents" : "content"}>
      <div className="content-top-spacer"></div>
      <div className="color-body manage reveal-product">
        <div className="page-head">
          <Link to="/my-projects" className="page-eyebrow mpv-back">
            ← My Projects
          </Link>
          <h1 className="page-title">
            {project ? project.name : error ? "Project" : <Skeleton width="16ch" height="1em" className="sk-inline" />}
          </h1>
        </div>

        {error ? (
          <div className="mp-empty">
            <span className="mp-empty-title">{error}</span>
            <span className="mp-empty-sub">
              It may have been removed, or you are no longer on it.
            </span>
          </div>
        ) : (
          <div className="reveal-swap" aria-busy={!project}>
            <BodySkeleton />
            {project ? (
              <div className="mpv-body">
                <aside className="mpv-facts card-input reveal-items">
                  {project.technology?.length ? (
                    <div className="mpv-fact">
                      <span className="mpv-fact-label">Technology</span>
                      <span className="mpv-fact-value">
                        {project.technology.map((t) => t.label).join(" · ")}
                      </span>
                    </div>
                  ) : null}
                  <People label="Managers" people={project.managers} />
                  <People label="Developers" people={project.developers} />
                  <LinkFact label="Development link" href={project.development_link} />
                  <LinkFact label="Live link" href={project.live_link} />
                  {project.updated_at ? (
                    <div className="mpv-fact">
                      <span className="mpv-fact-label">Last updated</span>
                      <span className="mpv-fact-value">
                        {moment.unix(project.updated_at).format("D MMM YYYY")}
                      </span>
                    </div>
                  ) : null}
                </aside>
                <article className="mpv-doc card-input">
                  {project.description ? (
                    <MarkdownView source={project.description} />
                  ) : (
                    <p className="mpv-nodoc">No description has been written for this project yet.</p>
                  )}
                </article>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyProjectView;
